import React, { FC, useCallback } from 'react';
import { useDemoSlot } from '../../hooks/useDemoSlot';
import { useApiClient } from '../../hooks/useApiClient';
import { Button } from '../shared/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPlayCircle, faPauseCircle } from '@fortawesome/free-solid-svg-icons';
import styled from '@emotion/styled';
import theme from '../shared/theme';

type Props = {
  hackathonId: number;
};

export const CurrentDemoSlotEditor: FC<Props> = ({ hackathonId }) => {
  const { demoSlot } = useDemoSlot();
  const apiClient = useApiClient();

  const nextDemoSlot = useCallback(async () => {
    if (!demoSlot) return;

    try {
      const currentSlot = demoSlot.slot === undefined || demoSlot.slot === null ? -1 : demoSlot.slot;
      await apiClient.hackathon.setCurrentDemoSlot(hackathonId, currentSlot + 1);
    } catch (err: any) {
      const is404 = err && err.status === 404;
      if (!is404) throw err;
    }
  }, [apiClient.hackathon, demoSlot, hackathonId]);

  const previousDemoSlot = useCallback(async () => {
    if (!demoSlot) return;

    if (!demoSlot.slot) await apiClient.hackathon.clearCurrentDemo(hackathonId);
    else await apiClient.hackathon.setCurrentDemoSlot(hackathonId, demoSlot.slot - 1);
  }, [demoSlot, apiClient, hackathonId]);

  if (!demoSlot) return null;

  const displaySlot = demoSlot.slot === undefined || demoSlot.slot === null ? '-' : demoSlot.slot + 1;
  const displayName = demoSlot.project ? demoSlot.project.name : 'No demo active';

  const disabled = !demoSlot.isLive;

  return (
    <Card>
      <Header>
        <StatusIndicator isLive={demoSlot.isLive}>
          <FontAwesomeIcon icon={demoSlot.isLive ? faPlayCircle : faPauseCircle} />
          <StatusText>{demoSlot.isLive ? 'Demo Active' : 'Demo Paused'}</StatusText>
        </StatusIndicator>
        <SlotInfo>
          <SlotLabel>Demo #{displaySlot}</SlotLabel>
        </SlotInfo>
      </Header>
      <ProjectName>{displayName}</ProjectName>
      <Controls>
        <NavButton size="md" onClick={previousDemoSlot} disabled={disabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <ButtonText>Previous</ButtonText>
        </NavButton>
        <NavButton size="md" onClick={nextDemoSlot} category="primary" disabled={disabled}>
          <ButtonText>Next</ButtonText>
          <FontAwesomeIcon icon={faChevronRight} />
        </NavButton>
      </Controls>
    </Card>
  );
};

const Card = styled.div`
  ${theme.styles.materialPanel};
  background: #fff;
  margin-bottom: 20px;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const StatusIndicator = styled.div<{ isLive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  background: ${props => (props.isLive ? theme.colors.green : theme.colors.lightGrey)};
  color: white;
  font-weight: 600;
  font-size: 12px;
  transition: background 0.3s ease;
`;

const StatusText = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SlotInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SlotLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.blue};
`;

const ProjectName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  min-height: 27px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NavButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 45px;
`;

const ButtonText = styled.span`
  font-weight: 600;
`;
